import { useMemo } from 'react'

import { amber, green, grey, orange, red } from '@mui/material/colors'
import { GridCellParams, GridRowModel, GridSortItem } from '@mui/x-data-grid'
import clsx from 'clsx'
import { differenceInMinutes, format } from 'date-fns'
import { useHistory } from 'react-router-dom'

import { FplDataGrid, IGridColumn } from 'components/common'
import { DATE_TIME_FORMAT, ETA_POLLING_INTERVAL_MS, Paths } from 'constants/index'
import { calculateDeliveryWindow } from 'constants/TrackingMap'
import { DriverLocationStatus } from 'generated/graphql'
import {
  GET_PAGED_BOOKINGS_TRACKING_DASHBOARD_DATA,
  GET_PAGED_DRIVER_BOOKINGS_TRACKING_DASHBOARD_DATA,
} from 'graphql/queries'
import { usePermission } from 'providers'

import { DriverStatus } from './DriverStatusEnum'
import WecAiSchedulersSwitch from './WecAiSchedulersSwitch'

function formatDriverStatusDetails(
  status: string,
  postCode: string,
  nextAddressDate: string,
): string {
  let formatedDate = ''
  let minutes = 0

  if (nextAddressDate) {
    formatedDate = format(new Date(nextAddressDate), DATE_TIME_FORMAT)
    minutes = differenceInMinutes(new Date(), new Date(nextAddressDate))
  }
  switch (status) {
    case DriverStatus.Unknown:
      return `Next stop ${postCode} has no time information`
    case DriverStatus.OnTime:
      return `Next stop ${postCode} by ${formatedDate}`
    case DriverStatus.Risk:
      return `Estimated to miss stop ${postCode} by ${formatedDate}`
    case DriverStatus.Late:
      return `Missed last stop ${postCode} by ${minutes} minutes`
    default:
      return `Unknown`
  }
}

const descendingOrderByDriverStatus: GridSortItem = {
  field: 'bookingRef',
  sort: 'desc',
}

const DefaultOrder: GridSortItem = {
  field: 'id',
  sort: 'asc',
}

const columns: IGridColumn[] = [
  {
    field: 'bookingRef',
    headerName: 'Booking Ref',
    sortPath: ['id'],
    flex: 0.3,
    valueGetter(params) {
      return params.row.ourReference
    },
  },
  {
    field: 'statusLabel',
    headerName: 'Booking Status',
    sortPath: ['status'],
    flex: 0.3,
    valueGetter(params) {
      return params.row.statusLabel
    },
  },
  {
    field: 'customer',
    headerName: 'Customer',
    sortPath: ['customerName'],
    flex: 0.3,
    valueGetter(params) {
      return params.row.customerName
    },
  },
  {
    field: 'customerRef',
    headerName: 'Customer Ref',
    sortPath: ['customerReference'],
    flex: 0.3,
    valueGetter(params) {
      return params.row.customerReference ?? 'N/A'
    },
  },
  {
    field: 'driverLocationStatus',
    headerName: 'Journey Stage',
    sortPath: ['driverStatus', 'locationStatus'],
    cellClassName: (params: GridCellParams<GridRowModel>) =>
      clsx({
        onTime: params.row.driverStatus.locationStatus === DriverLocationStatus.OnTime,
        waitingToBeLoaded:
          params.row.driverStatus.locationStatus === DriverLocationStatus.WaitingToBeLoaded,
        waitingToBeDelivered:
          params.row.driverStatus.locationStatus === DriverLocationStatus.WaitingToBeDelivered,
        lateToCollection:
          params.row.driverStatus.locationStatus === DriverLocationStatus.LateToCollection,
        lateToDelivery:
          params.row.driverStatus.locationStatus === DriverLocationStatus.LateToDelivery,
        notArrived: params.row.driverStatus.locationStatus === DriverLocationStatus.NotArrived,
      }),
    flex: 0.5,
    valueGetter(params) {
      return params.row.driverStatus.locationStatusLabel
    },
  },
  {
    field: 'driverStatus',
    headerName: 'Schedule Status',
    sortPath: ['driverStatus', 'status'],
    cellClassName: (params: GridCellParams<GridRowModel>) =>
      clsx({
        onTime: params.row.driverStatus.status === DriverStatus.OnTime,
        risk: params.row.driverStatus.status === DriverStatus.Risk,
        late: params.row.driverStatus.status === DriverStatus.Late,
        unknown: params.row.driverStatus.status === DriverStatus.Unknown,
      }),
    flex: 0.3,
    valueGetter(params) {
      return params.row.driverStatus.statusLabel
    },
  },
  {
    field: 'driverStatusDetails',
    headerName: 'Schedule Status Details',
    sortable: false,
    flex: 0.8,
    valueGetter(params) {
      const driverParams = params.row.driverStatus
      return formatDriverStatusDetails(
        driverParams.status,
        driverParams.postcode,
        driverParams.nextAddressDate,
      )
    },
  },
  {
    field: 'nextStopETA',
    headerName: "Driver's Next Stop ETA",
    sortable: false,
    flex: 0.5,
    valueGetter(params) {
      const { nextAddressETA, etaWindowMinutes } = params.row.driverStatus

      return nextAddressETA
        ? calculateDeliveryWindow(nextAddressETA, etaWindowMinutes ?? 30).timeRange
        : 'ETA not available'
    },
  },
]

function BookingsTracking() {
  const history = useHistory()

  const handleRowClick = (row) => {
    history.push(Paths.bookings.updateWithId(row.id))
  }

  const [isAllowedToViewAdminBookingsTrackingDashboard] = usePermission(
    'ViewAdminBookingsTrackingDashboard',
  )

  const [isAllowedToViewDriverBookingsTrackingDashboard] = usePermission(
    'ViewDriverBookingsTrackingDashboard',
  )

  const [isAllowedToViewAndChangeAppFeature] = usePermission('UpdateAppFeature')

  const query = useMemo(() => {
    if (isAllowedToViewAdminBookingsTrackingDashboard) {
      return GET_PAGED_BOOKINGS_TRACKING_DASHBOARD_DATA
    }
    if (isAllowedToViewDriverBookingsTrackingDashboard) {
      return GET_PAGED_DRIVER_BOOKINGS_TRACKING_DASHBOARD_DATA
    }
  }, [
    isAllowedToViewAdminBookingsTrackingDashboard,
    isAllowedToViewDriverBookingsTrackingDashboard,
  ])

  const entityName = useMemo(() => {
    if (isAllowedToViewAdminBookingsTrackingDashboard) {
      return 'bookingTrackingDashboardData'
    }
    if (isAllowedToViewDriverBookingsTrackingDashboard) {
      return 'driverBookingTrackingDashboardData'
    }
    return ''
  }, [
    isAllowedToViewAdminBookingsTrackingDashboard,
    isAllowedToViewDriverBookingsTrackingDashboard,
  ])

  return (
    <FplDataGrid
      pollInterval={ETA_POLLING_INTERVAL_MS}
      query={query}
      isHideOldData={true}
      refetchWithDelay
      entityName={entityName}
      defaultOrder={descendingOrderByDriverStatus}
      variableSortOrder={DefaultOrder}
      toolbar={{
        caption: 'Bookings Tracking',
        refreshButton: true,
        rightSide: isAllowedToViewAndChangeAppFeature ? <WecAiSchedulersSwitch /> : <></>,
      }}
      onRowClick={handleRowClick}
      columns={columns}
      rootSx={{
        '& .onTime': { backgroundColor: green[300] },
        '& .risk': { backgroundColor: amber[300] },
        '& .late': { backgroundColor: red[300] },
        '& .unknown': { backgroundColor: grey[300] },
        '& .notArrived': { backgroundColor: grey[100] },
        '& .waitingToBeLoaded': { backgroundColor: amber[100] },
        '& .waitingToBeDelivered': { backgroundColor: amber[100] },
        '& .lateToCollection': { backgroundColor: orange[200] },
        '& .lateToDelivery': { backgroundColor: orange[200] },
      }}
    />
  )
}

export default BookingsTracking
